import React, { useState, useMemo } from 'react';
import { Button, Grid } from 'semantic-ui-react';
import { resolveExtension } from '@plone/volto/helpers/Extensions/withBlockExtensions';
import config from '@plone/volto/registry';
import {
  hasNonValueOperation,
  hasDateOperation,
} from '@plone/volto/components/manage/Blocks/Search/utils';
import { defineMessages, useIntl } from 'react-intl';

/**
 * SHADOW of @plone/volto Search Facets.
 *
 * Only change vs core: when a facet's INTERACTIVE state is untouched, seed its
 * displayed value from the block's BASE QUERY (`query_to_values`) so filters
 * applied by the page/preset — e.g. the glossary family
 * `portal_type in [Definition, Acronym]` — show as SELECTED VALUES in the
 * left facet widget, not merely as the summary chips above the results
 * (the Image #1 request). Once the user interacts, `facets[field]` is defined
 * and takes precedence, so this never fights an explicit selection or clear.
 *
 * Second change (2026-09-27): a ROUTED facet (`facetRoutes`, passed by
 * LeftColumnFacets from presets.ts `facetPresetRoutes`) offers every value
 * that has a page, in registry order, on every page. Core narrows a facet's
 * choices to the base query's values, which on a series page left a one-item
 * Series menu with nothing else to pick. Registry order is numeric for the
 * directives series (0000, 100, 200 ... 1100), where the alphabetical sort
 * below put 1100 before 200.
 *
 * The sibling `../utils` import is rewritten to the absolute `@plone/volto`
 * path (Volto customization rule).
 */
const messages = defineMessages({
  moreFilters: { id: 'More filters', defaultMessage: 'More filters' },
  lessFilters: { id: 'Less filters', defaultMessage: 'Less filters' },
  showFilters: { id: 'Show filters', defaultMessage: 'Show filters' },
  hideFilters: { id: 'Hide filters', defaultMessage: 'Hide filters' },
});

const defaultShowFacet = (index) => {
  const { values } = index;
  return index
    ? hasNonValueOperation(index.operations || []) ||
      hasDateOperation(index.operations || [])
      ? true
      : values && Object.keys(values).length > 0
    : values && Object.keys(values).length > 0;
};

export const sortFacetChoices = (choices) => {
  const sorted_choices = choices.sort((a, b) =>
    typeof a.label === 'string' && typeof b.label === 'string'
      ? a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
      : typeof a.label === 'number' && typeof b.label == 'number'
        ? a.label - b.label
        : 0,
  );

  return sorted_choices;
};

const Facets = (props) => {
  const [hidden, setHidden] = useState(true);
  const {
    querystring,
    data = {},
    facets,
    setFacets,
    facetWrapper,
    isEditMode,
    facetRoutes = {},
  } = props;
  const { search } = config.blocks.blocksConfig;

  const advancedFilters = useMemo(() => {
    let count = 0;
    for (let facetSettings of data.facets || []) {
      if (facetSettings.advanced) {
        count++;
      }
    }

    if (count === data.facets?.length) {
      return 2;
    }
    if (count) {
      return 1;
    }
    return 0;
  }, [data.facets]);

  // item 28: aria-controls for the "More filters" toggle -- the ids match
  // FacetWrapper's `advanced-facet-<field>` (LeftColumnFacets.jsx), one per
  // advanced facet, since there is no single wrapping element to point at.
  const advancedFacetIds = useMemo(
    () =>
      (data.facets || [])
        .filter((facetSettings) => facetSettings.advanced && facetSettings.field?.value)
        .map((facetSettings) => `advanced-facet-${facetSettings.field.value}`)
        .join(' '),
    [data.facets],
  );

  const FacetWrapper = facetWrapper;
  const query_to_values = Object.assign(
    {},
    ...(data?.query?.query?.map(({ i, v }) => ({ [i]: v })) || []),
  );
  const intl = useIntl();

  return (
    <>
      {data?.facets
        ?.filter((facetSettings) => !facetSettings.hidden)
        .map((facetSettings) => {
          const field = facetSettings?.field?.value;
          const isAdvanced = facetSettings?.advanced;
          const index = querystring.indexes[field] || {};
          const { values = {} } = index;

          const route = facetRoutes[field];
          let choices;
          if (route) {
            // **edit (second change in the header): every routed value, in
            // registry order, whatever the base query holds.
            choices = Object.keys(route).map((name) => ({
              value: name,
              label: values[name]?.title || name,
            }));
          } else {
            choices = Object.keys(values)
              .map((name) => ({
                value: name,
                label: values[name].title,
              }))
              // filter the available values based on the allowed values in the
              // base query
              .filter(({ value }) =>
                query_to_values[field]
                  ? query_to_values[field].includes(value)
                  : true,
              );

            choices = sortFacetChoices(choices);
          }

          const isMulti = facetSettings.multiple;
          // **edit: seed the widget's value from the BASE QUERY when the facet
          // is UNTOUCHED so page/preset-applied filters render as selected
          // values (Image #1). Volto initializes base-query facets to an EMPTY
          // value ('' / []), so undefined is not enough — treat '' and [] as
          // untouched too, or the seed is masked. For a single-select facet,
          // unwrap a base-query array to its scalar (a `.any` base query stores
          // even one value as an array, which a single react-select can't show;
          // genuinely multi-valued fields were promoted to multiple upstream).
          const interactive = facets[field];
          const isEmptyFacet = (v) =>
            v === undefined ||
            v === null ||
            v === '' ||
            (Array.isArray(v) && v.length === 0);
          let selectedValue = !isEmptyFacet(interactive)
            ? interactive
            : query_to_values[field] !== undefined
              ? query_to_values[field]
              : interactive;
          // A single select cannot show an array, whichever side it came
          // from: the base-query seed, or facet state seeded from a `.any`
          // base row (withSearch re-seeds that on a preset switch).
          if (
            !isMulti &&
            Array.isArray(selectedValue) &&
            selectedValue.length === 1
          ) {
            selectedValue = selectedValue[0];
          }
          const visible = (isAdvanced && !hidden) || !isAdvanced;

          // TODO :handle changing the type of facet (multi/nonmulti)

          const {
            view: FacetWidget,
            stateToValue,
            showFacet = defaultShowFacet,
          } = resolveExtension(
            'type',
            search.extensions.facetWidgets.types,
            facetSettings,
          );

          let value = stateToValue({ facetSettings, index, selectedValue });

          const { rewriteOptions = (name, options) => options } =
            search.extensions.facetWidgets;

          return FacetWrapper && (isEditMode || showFacet(index)) ? (
            <FacetWrapper
              key={facetSettings['@id']}
              facetSettings={facetSettings}
              visible={visible}
            >
              <FacetWidget
                facet={facetSettings}
                choices={rewriteOptions(facetSettings?.field?.value, choices)}
                isMulti={isMulti}
                value={value}
                isEditMode={isEditMode}
                onChange={(id, value) => {
                  !isEditMode && setFacets({ ...facets, [id]: value });
                }}
              />
            </FacetWrapper>
          ) : (
            ''
          );
        })}
      {advancedFilters > 0 && (
        <Grid.Column
          mobile={12}
          tablet={12}
          computer={12}
          className="toggle-advanced-facets"
        >
          <Button
            type="button"
            aria-expanded={!hidden}
            aria-controls={advancedFacetIds || undefined}
            onClick={() => {
              setHidden((prevHidden) => !prevHidden);
            }}
          >
            {hidden
              ? advancedFilters === 2
                ? intl.formatMessage(messages.showFilters)
                : intl.formatMessage(messages.moreFilters)
              : advancedFilters === 2
                ? intl.formatMessage(messages.hideFilters)
                : intl.formatMessage(messages.lessFilters)}
          </Button>
        </Grid.Column>
      )}
    </>
  );
};

export default Facets;
